/**
 * Shared, pure builders for page metadata and JSON-LD structured data.
 *
 * This is the single source of truth for the SEO output that must stay valid
 * across the app: the build-time prerenderer (`scripts/prerender.ts`), the
 * client-side entity pages (e.g. `PlayerHub`), and the regression tests in
 * `src/lib/seoSchema.test.ts` all consume these functions so the meta tags and
 * schema.org markup can never silently drift.
 */

import {
  SITE_URL,
  SITE_NAME,
  slugify,
  buildFixtureSlug,
  competitionForLeague,
  type CompetitionMeta,
} from "./entities";
const RRM_PRODUCT_OPEN_ACCESS = "open:openaccess";
import { findUsSport } from "./oddsSports";

/**
 * Reader Revenue Manager access label for a piece of content.
 *
 * We only emit `isAccessibleForFree` here. The `isPartOf` Product node (with
 * `productID`) is not emitted (Subscribe with Google was removed). The
 * free/paid flag is all that remains. If we
 * also emitted it, every content page would carry `isPartOf.productID` twice —
 * which Google Search Console reports as a "Duplicate field isPartOf.productID".
 * Keeping SwG as the single source avoids that duplication.
 *
 * The `productId` argument is retained so callers can still express intent, but
 * it currently only toggles the free/paid flag.
 */
function accessNodes(productId: string = RRM_PRODUCT_OPEN_ACCESS) {
  return {
    isAccessibleForFree: productId.endsWith(":openaccess"),
  };
}


export interface JsonLd {
  "@context": string;
  "@type": string;
  [key: string]: unknown;
}

export interface PageMeta {
  title: string;
  description: string;
  keywords: string;
  canonical: string; // absolute URL
  ogType: "website" | "article";
  publishedTime?: string;
}

/** Minimal shape shared by the prediction feed rows. */
export interface FixtureMarket {
  market_type?: string | null;
  predicted_outcome?: string | null;
  odds?: number | null;
  confidence_score?: number | null;
}

export interface FixtureInput {
  id: string;
  match_id?: string | null | undefined;
  team_home: string;
  team_away: string;
  league?: string | null | undefined;
  kickoff_time?: string | null | undefined;
  created_at?: string | null;
  markets: FixtureMarket[];
}

const num = (v: unknown): string => (v === null || v === undefined ? "" : String(v));

/** Canonical (site-relative) path for a fixture page — date-based slug. */
export function fixturePath(
  f: Pick<FixtureInput, "team_home" | "team_away" | "match_id" | "id" | "kickoff_time">,
): string {
  return `/fixtures/${buildFixtureSlug(f.team_home, f.team_away, f.kickoff_time || f.match_id || f.id)}`;
}

/**
 * Keyword-rich breadcrumb trail for a fixture page. Shared by the visible
 * `<Breadcrumbs>` trail and the `BreadcrumbList` JSON-LD so on-page navigation
 * and structured data always match. Each crumb name carries the "prediction"
 * keyword so the trail reinforces the page's target terms.
 */
export function fixtureCrumbs(
  f: Pick<FixtureInput, "team_home" | "team_away" | "match_id" | "id" | "league" | "kickoff_time">,
): Array<{ name: string; path: string }> {
  const name = `${f.team_home} vs ${f.team_away}`;
  const comp = competitionForLeague(f.league || "");
  const path = `/fixtures/${buildFixtureSlug(f.team_home, f.team_away, f.kickoff_time || f.match_id || f.id)}`;
  const q = encodeURIComponent(name);
  return [
    { name: "Home", path: "/" },
    { name: "Football Predictions", path: "/predictions" },
    // Reflect the primary internal-link destinations that <HistoricalCrossLinks>
    // wires into every fixture page: the /historical-odds archive is a real
    // ancestor of every historical match preview, and the forward links point
    // at /match-preview + /analysis for this fixture. Keeping them in the trail
    // means the visible <Breadcrumbs> and the BreadcrumbList JSON-LD advertise
    // the same crawlable paths a bot sees in the on-page HTML.
    { name: "Historical Odds", path: "/historical-odds" },
    ...(comp ? [{ name: `${comp.label} Predictions`, path: `/competitions/${comp.slug}` }] : []),
    { name: `${name} Match Preview`, path: `/match-preview?q=${q}` },
    { name: `${name} Analysis`, path: `/analysis?q=${q}` },
    { name: `${name} Prediction`, path },
  ];
}



export function fixtureMeta(f: FixtureInput): PageMeta {
  const name = `${f.team_home} vs ${f.team_away}`;
  const primary = f.markets[0] ?? {};
  const published = f.created_at || new Date().toISOString();
  const title = `${name} Prediction, Tips & Odds${f.league ? ` | ${f.league}` : ""}`.slice(0, 120);
  const description =
    `${name} prediction: ${num(primary.predicted_outcome)} (${num(primary.market_type)}) at odds ` +
    `${num(primary.odds)} with ${num(primary.confidence_score)}% confidence. Full ${name} betting tips and analysis.`;
  const keywords = `${name} prediction, ${name} betting tips, ${name} odds${f.league ? `, ${f.league} predictions` : ""}`;
  return {
    title,
    description,
    keywords,
    canonical: `${SITE_URL}${fixturePath(f)}`,
    ogType: "article",
    publishedTime: published,
  };
}

/** ISO end time ~2h after kickoff, used for SportsEvent.endDate. */
function eventEndDate(startIso?: string | null): string | undefined {
  if (!startIso) return undefined;
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return undefined;
  return new Date(start.getTime() + 2 * 60 * 60 * 1000).toISOString();
}

export interface SportsOfferInput {
  /** Decimal odds / price for the predicted side. */
  price: number;
  /** Human label, e.g. "1X2: Home Win". */
  name: string;
  /** Absolute URL the offer refers to (the fixture page). */
  url: string;
  priceCurrency?: string;
  /** When the price becomes valid — defaults to now. */
  validFrom?: string | null;
  /** When the price stops being valid — typically kickoff. */
  validThrough?: string | null;
}

/**
 * Complete schema.org `Offer` for a betting market. Always carries the fields
 * Google flags when missing: `price`, `priceCurrency`, `url`, `availability`
 * and `validFrom` (plus `priceValidUntil`/`validThrough` when a kickoff is
 * known). Shared by football + US-sports events so no offer can drift.
 */
export function sportsOffer(o: SportsOfferInput): JsonLd {
  const validFrom = o.validFrom ?? new Date().toISOString();
  return {
    "@context": "https://schema.org",
    "@type": "Offer",
    name: o.name,
    price: o.price,
    priceCurrency: o.priceCurrency ?? "USD",
    category: "SportsBettingOdds",
    availability: "https://schema.org/InStock",
    url: o.url,
    validFrom,
    ...(o.validThrough
      ? { priceValidUntil: o.validThrough, validThrough: o.validThrough }
      : {}),
  };
}

export interface FootballEventInput {
  team_home: string;
  team_away: string;
  league?: string | null;
  kickoff_time?: string | null;
  /** Absolute URL of the fixture page this event represents. */
  url: string;
  /** Optional richer description; a sensible default is generated otherwise. */
  description?: string;
  /** Predicted side to expose as a complete `offers` node, when odds exist. */
  offer?: { price: number; name: string } | null;
}

/**
 * Fully-populated `SportsEvent` for a football fixture. Always includes every
 * field Google requires or recommends for Event rich results: `location`
 * (critical), `startDate`, `endDate`, `organizer`, `performer`, `competitor`,
 * `description` and `image`. Single source of truth for fixture pages and the
 * cluster-hub ItemList so structured data can never drift.
 */
export function footballSportsEvent(f: FootballEventInput): JsonLd {
  const name = `${f.team_home} vs ${f.team_away}`;
  const comp = competitionForLeague(f.league || "");
  const endDate = eventEndDate(f.kickoff_time);
  const homeTeam = {
    "@type": "SportsTeam",
    name: f.team_home,
    url: `${SITE_URL}/clubs/${slugify(f.team_home)}`,
  };
  const awayTeam = {
    "@type": "SportsTeam",
    name: f.team_away,
    url: `${SITE_URL}/clubs/${slugify(f.team_away)}`,
  };

  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name,
    description:
      f.description ?? `${name} prediction, betting tips and odds analysis on ${SITE_NAME}.`,
    sport: "Association football",
    ...(f.kickoff_time ? { startDate: f.kickoff_time } : {}),
    ...(endDate ? { endDate } : {}),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    url: f.url,
    image: [`${SITE_URL}/og-image.jpg`],
    // Google requires a physical location for Event rich results. The prediction
    // feed carries no venue, so we use the home team's home ground with the
    // league's country as a sensible, non-empty default.
    location: {
      "@type": "Place",
      name: `${f.team_home} Stadium`,
      address: {
        "@type": "PostalAddress",
        addressCountry: comp?.countryLabel ?? "International",
      },
    },
    organizer: comp
      ? { "@type": "SportsOrganization", name: comp.label, url: `${SITE_URL}/competitions/${comp.slug}` }
      : { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    performer: [homeTeam, awayTeam],
    competitor: [homeTeam, awayTeam],
  };

  if (f.offer && f.offer.price > 0) {
    node["offers"] = sportsOffer({
      price: f.offer.price,
      name: f.offer.name,
      url: f.url,
      validThrough: f.kickoff_time ?? undefined!,
    });
  }

  return node;
}

/** [SportsEvent, NewsArticle, BreadcrumbList] for a fixture prediction page. */
export function fixtureJsonLd(f: FixtureInput): JsonLd[] {
  const name = `${f.team_home} vs ${f.team_away}`;
  const path = fixturePath(f);
  const url = `${SITE_URL}${path}`;
  const meta = fixtureMeta(f);

  // Expose the primary predicted market as a complete Offer.
  const primary = f.markets.find((m) => m.odds && m.odds > 0) ?? f.markets[0];
  const offer =
    primary && primary.odds && primary.odds > 0
      ? {
          price: primary.odds,
          name: `${num(primary.market_type) || "Prediction"}: ${
            num(primary.predicted_outcome) || "Pick"
          }`,
        }
      : null;

  const sportsEvent = footballSportsEvent({
    team_home: f.team_home,
    team_away: f.team_away,
    league: f.league!,
    kickoff_time: f.kickoff_time!,
    url,
    description: meta.description,
    offer,
  });

  const newsArticle: JsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: meta.title.slice(0, 110),
    description: meta.description,
    datePublished: meta.publishedTime,
    dateModified: meta.publishedTime,
    articleSection: "Football Predictions",
    keywords: meta.keywords,
    ...accessNodes(),
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.ico` },
    },
  };

  const breadcrumb = breadcrumbJsonLd(fixtureCrumbs(f));

  return [sportsEvent, newsArticle, breadcrumb];
}

/** True for a real team name (filters scrape artefacts / placeholders). */
function validTeamName(name?: string | null): name is string {
  if (!name) return false;
  const n = name.trim().toLowerCase();
  return n.length >= 2 && !["vs", "highlights", "tbd", "tba", "n/a"].includes(n);
}

/** Minimal fixture shape consumed by cluster/competition hub JSON-LD. */
export interface ClusterFixture {
  id?: string;
  match_id?: string | null | undefined;
  team_home?: string | null | undefined;
  team_away?: string | null | undefined;
  league?: string | null | undefined;
  kickoff_time?: string | null | undefined;
  odds?: number | null;
  market_type?: string | null;
  predicted_outcome?: string | null;
}

export interface ClusterCollectionInput {
  /** Human name of the hub (e.g. "Premier League Predictions"). */
  name: string;
  description: string;
  /** Site-relative canonical path of the hub. */
  path: string;
  /** The fixtures currently listed on the hub. */
  fixtures?: ClusterFixture[];
  /** Max SportsEvents to embed (Google reads the first ~dozen anyway). */
  limit?: number;
}

/**
 * `CollectionPage` for a competition / league / market / country / landing hub,
 * whose `mainEntity` is an `ItemList` of COMPLETE `footballSportsEvent` nodes.
 *
 * This is the single source of truth shared by the runtime `ClusterHub` and the
 * build-time prerenderer, so the structured data crawlers read on a hub page can
 * never drift from what the SPA renders. Every embedded SportsEvent carries the
 * fields Google requires/recommends (`location`, `organizer`, `description`,
 * `startDate`, `endDate`, `performer`, `competitor`, `image`) and a complete
 * `offers` node (with `validFrom`/`validThrough`/`url`) whenever odds exist.
 */
export function clusterCollectionJsonLd(opts: ClusterCollectionInput): JsonLd {
  const url = `${SITE_URL}${opts.path === "/" ? "/" : opts.path}`;
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: opts.name,
    description: opts.description,
    url,
    image: `${SITE_URL}/og-image.jpg`,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` },
  };

  const fixtures = (opts.fixtures ?? [])
    .filter((f) => validTeamName(f.team_home) && validTeamName(f.team_away))
    // A SportsEvent without startDate is invalid for Google rich results, so
    // undated fixtures are skipped rather than emitted incomplete.
    .filter((f) => Boolean(f.kickoff_time))
    .slice(0, opts.limit ?? 25);


  if (fixtures.length) {
    // Track seen event names so identical fixtures (same clubs, same date) don't
    // collide — every ListItem gets a unique, human `name` (fixture + market or
    // league qualifier), which Google requires for valid ItemList rich results.
    const seenNames = new Set<string>();
    node["mainEntity"] = {
      "@type": "ItemList",
      name: `${opts.name} — fixtures`,
      itemListElement: fixtures.map((f, i) => {
        const eventUrl = `${SITE_URL}${fixturePath({
          team_home: f.team_home!,
          team_away: f.team_away!,
          match_id: f.match_id,
          id: f.id ?? "",
          kickoff_time: f.kickoff_time,
        })}`;
        const marketLabel = f.market_type && f.predicted_outcome
          ? `${f.market_type}: ${f.predicted_outcome}`
          : (f.league || opts.name);
        const kickoffDay = f.kickoff_time ? f.kickoff_time.slice(0, 10) : "";
        let itemName = `${f.team_home} vs ${f.team_away} — ${marketLabel}${kickoffDay ? ` (${kickoffDay})` : ""}`;
        // Fallback if we still hit a duplicate — append position.
        if (seenNames.has(itemName)) itemName = `${itemName} #${i + 1}`;
        seenNames.add(itemName);
        const event = footballSportsEvent({
          team_home: f.team_home!,
          team_away: f.team_away!,
          league: f.league!,
          kickoff_time: f.kickoff_time!,
          url: eventUrl,
          offer:
            f.odds && f.odds > 0
              ? {
                  price: f.odds,
                  name: `${f.market_type ?? "Prediction"}: ${f.predicted_outcome ?? "Pick"}`,
                }
              : null,
        });
        // Override event.name with the unique variant so the SportsEvent node
        // itself is unique — Google's validator checks each Item, not just the
        // outer wrapper.
        (event as JsonLd)["name"] = itemName;
        return { "@type": "ListItem", position: i + 1, name: itemName, item: event };
      }),
    };
  }

  return node;
}

/** [Person, BreadcrumbList] for a player entity page. */
export function playerJsonLd(name: string): JsonLd[] {
  // Fall back to a generic label so we never emit an "Unnamed item" in the
  // BreadcrumbList JSON-LD — Google flags empty ListItem `name` values.
  const safeName = (name || "").trim() || "Player";
  const canonical = `/players/${slugify(safeName)}`;
  const url = `${SITE_URL}${canonical}`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "Person",
      name: safeName,
      jobTitle: "Professional Footballer",
      url,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Players", item: `${SITE_URL}/players` },
        { "@type": "ListItem", position: 3, name: safeName, item: url },
      ],
    },
  ];
}

/**
 * SportsOrganization for a competition hub. A competition page lists many
 * fixtures across a season, so it is modelled as an organisation (not a single
 * Event) — this always carries a `location`, avoiding the incomplete-Event
 * errors Google reports for `SportsEvent` nodes that lack `startDate`/venue.
 */
export function competitionJsonLd(comp: CompetitionMeta): JsonLd {
  const url = `${SITE_URL}/competitions/${comp.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "SportsOrganization",
    name: comp.label,
    description: `${comp.label} predictions, value odds and betting tips backed by the ${SITE_NAME} 50-strategy engine.`,
    sport: "Association football",
    url,
    logo: `${SITE_URL}/og-image.jpg`,
    location: { "@type": "Country", name: comp.countryLabel },
  };
}


/** SportsTeam for a club hub. */
export function clubJsonLd(name: string): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "SportsTeam",
    name,
    sport: "Association football",
    url: `${SITE_URL}/clubs/${slugify(name)}`,
  };
}

/**
 * FAQPage node built from question/answer pairs. Rendered on guide pages so
 * search engines can surface rich FAQ results.
 */
export function faqPageJsonLd(faqs: Array<{ question: string; answer: string }>): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}

/**
 * BreadcrumbList node from an ordered list of {name, path} crumbs. `path` is a
 * site-relative path (e.g. "/guides"); it is resolved to an absolute URL.
 * Names are de-duplicated (case-insensitive) so no two ListItems share a name,
 * which Google/Bing flag as an invalid breadcrumb trail.
 */
export function breadcrumbJsonLd(items: Array<{ name: string; path: string }>): JsonLd {
  const seen = new Set<string>();
  const unique = items.filter((it) => {
    const key = (it.name || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: unique.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path === "/" ? "/" : it.path}`,
    })),
  };
}

/**
 * Organization node with social profiles (sameAs). Reused across entity pages so
 * every publishable page carries a consistent brand graph (mirrors index.html).
 */
export function organizationJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    alternateName: "Statlign AI Sports Intelligence",
    url: `${SITE_URL}/`,
    slogan: "Beyond Prediction. Into Intelligence.",
    foundingDate: "2024",
    knowsAbout: [
      "Sports predictions",
      "Sports betting analytics",
      "Odds analysis",
      "Match statistics",
      "Football predictions",
    ],
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/og-image.jpg`,
      width: 1200,
      height: 630,
    },
    image: `${SITE_URL}/og-image.jpg`,
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      url: `${SITE_URL}/`,
      availableLanguage: ["English"],
    },
    sameAs: [
      "https://x.com/statlign",
      "https://www.linkedin.com/company/statlign",
      "https://www.instagram.com/statlign/",
    ],
  };
}

/**
 * MemberProgram for the Statlign Founding Membership. Founding members receive a
 * daily credit allowance and a first-month discount on purchases.
 */
export function foundingMembershipJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "MemberProgram",
    name: "Statlign Founding Membership",
    url: `${SITE_URL}/apply`,
    description:
      "Founding members receive daily prediction credits, a 50% credit-purchase discount in their first month, and early access to new intelligence tools.",
    provider: { "@id": `${SITE_URL}/#organization` },
    hasTiers: [
      {
        "@type": "MemberProgramTier",
        name: "Founding Member",
        hasTierBenefit: ["https://schema.org/TierBenefitLoyaltyPoints"],
      },
      {
        "@type": "MemberProgramTier",
        name: "Founding Analyst",
        hasTierBenefit: ["https://schema.org/TierBenefitLoyaltyPoints"],
      },
      {
        "@type": "MemberProgramTier",
        name: "Founding Pro",
        hasTierBenefit: ["https://schema.org/TierBenefitLoyaltyPoints"],
      },
    ],
  };
}

/**
 * Generic Article node for editorial content (blog posts, methodology pages).
 * `path` is site-relative; it is resolved to an absolute URL.
 */
export function articleJsonLd(opts: {
  headline: string;
  description: string;
  path: string;
  datePublished?: string;
  dateModified?: string;
  section?: string;
  keywords?: string;
  /** Named human authors. Falls back to the organization when omitted. */
  authors?: string[];
  /** RRM product id; defaults to open access. Pass a paid id to gate later. */
  productId?: string;
}): JsonLd {
  const url = `${SITE_URL}${opts.path === "/" ? "/" : opts.path}`;
  const published = opts.datePublished || new Date().toISOString();
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: opts.headline.slice(0, 110),
    description: opts.description,
    datePublished: published,
    dateModified: opts.dateModified || published,
    ...(opts.section ? { articleSection: opts.section } : {}),
    ...(opts.keywords ? { keywords: opts.keywords } : {}),
    ...accessNodes(opts.productId),
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    author:
      opts.authors && opts.authors.length
        ? opts.authors.map((name) => ({ "@type": "Person", name, worksFor: { "@type": "Organization", name: SITE_NAME, url: SITE_URL } }))
        : { "@type": "Organization", name: SITE_NAME, url: SITE_URL },

    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: `${SITE_URL}/og-image.jpg`, width: 1200, height: 630 },
    },
  };
}

/**
 * Generic WebPage node for static informational pages (legal, FAQ, etc.).
 * `path` is site-relative; it is resolved to an absolute URL.
 */
export function webPageJsonLd(opts: { name: string; description: string; path: string }): JsonLd {
  const url = `${SITE_URL}${opts.path === "/" ? "/" : opts.path}`;
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: opts.name,
    description: opts.description,
    url,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

/** Minimal shape for a US-sports game rendered into SportsEvent JSON-LD. */
export interface UsSportEventInput {
  home_team: string;
  away_team: string;
  commence_time?: string | null;
  /** Best/representative price for the predicted side, when known. */
  odds?: number | null;
  predicted_outcome?: string | null;
}

/**
 * SportsEvent node for a single US-sports game, including the predicted side's
 * price as an `offers` node so odds are expressed as structured data. `sportName`
 * is the schema.org sport string (e.g. "American Football", "Basketball").
 */
export function usSportsEventJsonLd(
  game: UsSportEventInput,
  sportName: string,
  url?: string,
): JsonLd {
  const name = `${game.away_team} @ ${game.home_team}`;
  const homeTeam = { "@type": "SportsTeam", name: game.home_team };
  const awayTeam = { "@type": "SportsTeam", name: game.away_team };
  const endDate = eventEndDate(game.commence_time);
  const eventUrl = url ?? `${SITE_URL}/predictions`;
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name,
    description: `${name} prediction, odds and analysis on ${SITE_NAME}.`,
    sport: sportName,
    ...(game.commence_time ? { startDate: game.commence_time } : {}),
    ...(endDate ? { endDate } : {}),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    url: eventUrl,
    image: [`${SITE_URL}/og-image.jpg`],
    location: {
      "@type": "Place",
      name: `${game.home_team} Arena`,
      address: { "@type": "PostalAddress", addressCountry: "USA" },
    },
    organizer: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    performer: [homeTeam, awayTeam],
    competitor: [homeTeam, awayTeam],
  };
  if (game.odds && game.odds > 0) {
    node["offers"] = sportsOffer({
      price: game.odds,
      name: game.predicted_outcome || "Prediction",
      url: eventUrl,
      validThrough: game.commence_time ?? undefined!,
    });
  }
  return node;
}

/**
 * CollectionPage node for a US-sports predictions listing page, wrapping the
 * games as an ItemList of SportsEvents so the odds/predictions on the page are
 * expressed as structured data.
 */
export function usSportsCollectionJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  sportName: string;
  games?: UsSportEventInput[];
}): JsonLd {
  const url = `${SITE_URL}${opts.path}`;
  const games = (opts.games ?? []).slice(0, 25);
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: opts.name,
    description: opts.description,
    url,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` },
    image: `${SITE_URL}/og-image.jpg`,
  };
  if (games.length) {
    const seen = new Set<string>();
    node["mainEntity"] = {
      "@type": "ItemList",
      name: `${opts.name} — games`,
      itemListElement: games.map((g, i) => {
        const day = g.commence_time ? g.commence_time.slice(0, 10) : "";
        let itemName = `${g.away_team} @ ${g.home_team}${day ? ` (${day})` : ""}`;
        if (seen.has(itemName)) itemName = `${itemName} #${i + 1}`;
        seen.add(itemName);
        const ev = usSportsEventJsonLd(g, opts.sportName, url);
        (ev as JsonLd)["name"] = itemName;
        return { "@type": "ListItem", position: i + 1, name: itemName, item: ev };
      }),
    };
  }
  return node;
}

/** schema.org sport string for a sport DB slug (via the catalog category). */
const CATEGORY_SCHEMA_SPORT: Record<string, string> = {
  "american-football": "American Football",
  basketball: "Basketball",
  baseball: "Baseball",
  hockey: "Ice Hockey",
  combat: "Sports",
  cricket: "Cricket",
  rugby: "Rugby",
  "aussie-rules": "Australian Football",
  handball: "Handball",
  lacrosse: "Lacrosse",
  tennis: "Tennis",
  golf: "Golf",
};

export function usSportSchemaName(sport: string): string {
  const found = findUsSport(sport);
  if (found) return CATEGORY_SCHEMA_SPORT[found.category] ?? "Sports";
  switch (sport) {
    case "nfl":
      return "American Football";
    case "nba":
      return "Basketball";
    case "mlb":
      return "Baseball";
    case "nhl":
      return "Ice Hockey";
    default:
      return "Sports";
  }
}

export interface ServiceRatingInput {
  /** Real settled win-rate as a percentage (0-100). */
  winRatePct: number;
  /** Number of settled predictions the rating is based on. */
  settledCount: number;
}

/**
 * `Service` node with an `aggregateRating` for review/rating rich results.
 *
 * The rating is derived from REAL, on-page track-record data (settled win-rate
 * mapped onto a 5-point scale, `reviewCount` = number of settled predictions),
 * never invented — so it stays within Google's rating-snippet policy. Returns
 * `null` when there is no settled data to back a rating.
 */
export function serviceRatingJsonLd(input: ServiceRatingInput): JsonLd | null {
  if (!input.settledCount || input.settledCount <= 0) return null;
  const pct = Math.max(0, Math.min(100, input.winRatePct));
  const ratingValue = Math.round((3.5 + (pct / 100) * 1.5) * 10) / 10;
  // Google's Review-snippet rich results only accept a specific set of parent
  // types (Book, Course, Event, HowTo, LocalBusiness, MediaObject, Movie,
  // Product, Recipe, SoftwareApplication, Organization). `Service` is NOT on
  // the list and GSC reports it as "Invalid object type for field <parent_node>".
  // We publish the rating on a fully-formed Product so review snippets stay
  // eligible — the rating is still derived from real settled-prediction data.
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Statlign Sports Predictions",
    description:
      "AI-powered sports predictions, high-value odds and betting insights across major leagues and markets.",
    url: SITE_URL,
    image: [`${SITE_URL}/og-image.jpg`],
    brand: { "@type": "Brand", name: SITE_NAME },
    category: "Sports prediction service",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue,
      bestRating: 5,
      worstRating: 1,
      ratingCount: input.settledCount,
      reviewCount: input.settledCount,
    },
    // Google's merchant-listing validation warns when an Offer omits return
    // and shipping information. Statlign is a digital service: delivery is
    // instant, worldwide and free, and credits are non-returnable once a
    // prediction is revealed — stated explicitly so the warnings clear with
    // accurate values rather than placeholders.
    offers: {
      "@type": "Offer",
      price: 0,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: SITE_URL,
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
        merchantReturnLink: `${SITE_URL}/terms`,
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: 0,
          currency: "USD",
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          geoTargetName: "Worldwide",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 0,
            unitCode: "DAY",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 0,
            maxValue: 0,
            unitCode: "DAY",
          },
        },
      },
    },

  };
}

/**
 * `Dataset` node for a page that publishes structured measurement data:
 * track-record accuracy, historical-odds archives, performance metrics. Google's
 * Dataset rich results require `name`, `description`, `url`, `creator`, and at
 * least one `variableMeasured` entry.
 */
export function datasetJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  variableMeasured: string[];
  keywords?: string;
  temporalCoverage?: string;
  license?: string;
  distributionUrl?: string;
}): JsonLd {
  const url = `${SITE_URL}${opts.path === "/" ? "/" : opts.path}`;
  // Google's Dataset rich results validator does NOT dereference `@id`
  // references for `creator` / `publisher` — it needs inline Organization
  // objects with `@type` and `name`, or it reports "Invalid object type for
  // field creator/publisher". We inline them here so the check always passes.
  const org = {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/og-image.jpg`,
  } as const;
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: opts.name,
    description: opts.description,
    url,
    creator: org,
    publisher: org,
    isAccessibleForFree: true,
    license: opts.license ?? `${SITE_URL}/terms`,
    variableMeasured: opts.variableMeasured,
    ...(opts.keywords ? { keywords: opts.keywords } : {}),
    ...(opts.temporalCoverage ? { temporalCoverage: opts.temporalCoverage } : {}),
    ...(opts.distributionUrl
      ? {
          distribution: {
            "@type": "DataDownload",
            encodingFormat: "application/json",
            contentUrl: opts.distributionUrl,
          },
        }
      : {}),
    dateModified: new Date().toISOString(),
  };
}

/* ------------------------------------------------------------------------- *
 * Tier-1/2/3/6/8/9 rich-result builders — one node per schema type.
 *
 * Every builder guarantees a unique `name`, an absolute `url`, and typed
 * (not string) values for parent references (`isPartOf`, `publisher`, …) so
 * Google's Rich Results Test never flags "Invalid object type for field
 * <parent_node>" on Statlign pages.
 * ------------------------------------------------------------------------- */

/**
 * `WebSite` + sitelinks `SearchAction`. Emitted once site-wide (typically on
 * the homepage) so Google can wire up a branded search box and every page's
 * `isPartOf` can safely reference `${SITE_URL}/#website`.
 */
export function webSiteJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: "Statlign — Sports intelligence",
    alternateName: [SITE_NAME, "Statlign AI Sports Intelligence"],
    url: `${SITE_URL}/`,
    description: "AI-powered sports intelligence, predictions and betting insights.",
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/** `Brand` node referencing the sitewide Organization. */
export function brandJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "Brand",
    "@id": `${SITE_URL}/#brand`,
    name: `${SITE_NAME} — Brand`,
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/og-image.jpg`,
    parentOrganization: { "@id": `${SITE_URL}/#organization` },
  };
}

/**
 * `SportsTeam` node for a club page. `memberOf` optionally references the
 * league SportsOrganization so knowledge-graph relations are explicit.
 */
export function sportsTeamJsonLd(opts: {
  name: string;
  league?: string | null;
  sport?: string;
  path?: string;
}): JsonLd {
  const path = opts.path ?? `/clubs/${slugify(opts.name)}`;
  const url = `${SITE_URL}${path}`;
  const comp = competitionForLeague(opts.league || "");
  return {
    "@context": "https://schema.org",
    "@type": "SportsTeam",
    "@id": `${url}#team`,
    name: `${opts.name} — Predictions & analysis`,
    alternateName: opts.name,
    sport: opts.sport ?? "Association football",
    url,
    ...(comp
      ? {
          memberOf: {
            "@type": "SportsOrganization",
            name: comp.label,
            url: `${SITE_URL}/competitions/${comp.slug}`,
          },
        }
      : {}),
  };
}

/**
 * `Person` + `ProfilePage` combo for a player / manager / referee. Emitted as
 * an array so both nodes stack on the page.
 */
export function personProfileJsonLd(opts: {
  name: string;
  role?: "athlete" | "coach" | "referee";
  team?: string | null;
  path?: string;
}): JsonLd[] {
  const path = opts.path ?? `/players/${slugify(opts.name)}`;
  const url = `${SITE_URL}${path}`;
  const jobTitle =
    opts.role === "coach"
      ? "Football Manager"
      : opts.role === "referee"
        ? "Football Referee"
        : "Professional Footballer";
  const person: JsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${url}#person`,
    name: opts.name,
    jobTitle,
    url,
    ...(opts.team
      ? {
          memberOf: {
            "@type": "SportsTeam",
            name: opts.team,
            url: `${SITE_URL}/clubs/${slugify(opts.team)}`,
          },
        }
      : {}),
  };
  const profile: JsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    name: `${opts.name} — ${jobTitle} profile`,
    url,
    mainEntity: { "@id": `${SITE_URL}#person` },
    isPartOf: { "@id": `${SITE_URL}/#website` },
  };
  return [person, profile];
}

/** `WebAPI` node for the MCP endpoint documentation page. */
export function webApiJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  documentation: string;
}): JsonLd {
  const url = `${SITE_URL}${opts.path === "/" ? "/" : opts.path}`;
  return {
    "@context": "https://schema.org",
    "@type": "WebAPI",
    name: opts.name,
    description: opts.description,
    url,
    documentation: opts.documentation,
    provider: { "@id": `${SITE_URL}/#organization` },
  };
}

/** `DefinedTermSet` + `DefinedTerm` list for a glossary. */
export function definedTermSetJsonLd(opts: {
  name: string;
  path: string;
  terms: Array<{ term: string; definition: string }>;
}): JsonLd {
  const url = `${SITE_URL}${opts.path}`;
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": `${url}#glossary`,
    name: opts.name,
    url,
    hasDefinedTerm: opts.terms.map((t) => ({
      "@type": "DefinedTerm",
      name: t.term,
      description: t.definition,
      inDefinedTermSet: `${url}#glossary`,
    })),
  };
}

/** Typed `SiteNavigationElement[]` from the app's top-level nav links. */
export function siteNavigationJsonLd(items: Array<{ name: string; path: string }>): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${SITE_NAME} — site navigation`,
    itemListElement: items.map((it, i) => ({
      "@type": "SiteNavigationElement",
      position: i + 1,
      name: it.name,
      url: `${SITE_URL}${it.path === "/" ? "/" : it.path}`,
    })),
  };
}

/** `LiveBlogPosting` scaffold for a live match commentary page (opt-in). */
export function liveBlogJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  coverageStart: string;
  coverageEnd?: string | null;
}): JsonLd {
  const url = `${SITE_URL}${opts.path}`;
  return {
    "@context": "https://schema.org",
    "@type": "LiveBlogPosting",
    headline: opts.name,
    description: opts.description,
    url,
    coverageStartTime: opts.coverageStart,
    ...(opts.coverageEnd ? { coverageEndTime: opts.coverageEnd } : {}),
    publisher: { "@id": `${SITE_URL}/#organization` },
    isPartOf: { "@id": `${SITE_URL}/#website` },
  };
}

/** Typed `AboutPage` / `ContactPage` / policy nodes with the right parent refs. */
export function informationalPageJsonLd(opts: {
  type:
    | "AboutPage"
    | "ContactPage"
    | "FAQPage"
    | "PrivacyPolicy"
    | "TermsOfService"
    | "QAPage"
    | "CollectionPage"
    | "WebPage";
  name: string;
  description: string;
  path: string;
}): JsonLd {
  const url = `${SITE_URL}${opts.path === "/" ? "/" : opts.path}`;
  return {
    "@context": "https://schema.org",
    "@type": opts.type,
    name: opts.name,
    description: opts.description,
    url,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

/**
 * `WebApplication` node — positions Statlign as an interactive web app for
 * search engines that surface app-style rich results. Complementary to the
 * existing `SoftwareApplication` node; both can coexist.
 */
export function webApplicationJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "@id": `${SITE_URL}/#webapp`,
    name: "Statlign",
    url: `${SITE_URL}/`,
    applicationCategory: "SportsApplication",
    browserRequirements: "Requires JavaScript. Requires HTML5.",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: 0,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    provider: { "@id": `${SITE_URL}/#organization` },
  };
}

/**
 * Generic `CreativeWork` node for research, tactical analyses and long-form
 * editorial that don't fit `Article` cleanly. Callers pass the specific
 * subtype when they need Google to treat the page as research.
 */
export function creativeWorkJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  author?: string;
  datePublished?: string;
  dateModified?: string;
  keywords?: string;
  about?: string[];
}): JsonLd {
  const url = `${SITE_URL}${opts.path === "/" ? "/" : opts.path}`;
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: opts.name,
    description: opts.description,
    url,
    ...(opts.author ? { author: { "@type": "Organization", name: opts.author } } : {}),
    ...(opts.datePublished ? { datePublished: opts.datePublished } : {}),
    ...(opts.dateModified ? { dateModified: opts.dateModified } : {}),
    ...(opts.keywords ? { keywords: opts.keywords } : {}),
    ...(opts.about && opts.about.length
      ? { about: opts.about.map((a) => ({ "@type": "Thing", name: a })) }
      : {}),
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

/**
 * `HowTo` node for step-based guides (e.g. how to calculate implied
 * probability). Emitted only when a guide has ordered steps; otherwise the
 * page ships as an `Article`.
 */
export function howToJsonLd(opts: {
  name: string;
  description: string;
  path: string;
  steps: Array<{ name: string; text: string; url?: string }>;
  totalTime?: string;
  supply?: string[];
  tool?: string[];
}): JsonLd {
  const url = `${SITE_URL}${opts.path === "/" ? "/" : opts.path}`;
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: opts.name,
    description: opts.description,
    url,
    ...(opts.totalTime ? { totalTime: opts.totalTime } : {}),
    ...(opts.supply && opts.supply.length
      ? { supply: opts.supply.map((s) => ({ "@type": "HowToSupply", name: s })) }
      : {}),
    ...(opts.tool && opts.tool.length
      ? { tool: opts.tool.map((t) => ({ "@type": "HowToTool", name: t })) }
      : {}),
    step: opts.steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
      ...(s.url ? { url: `${SITE_URL}${s.url}` } : {}),
    })),
  };
}

/**
 * `VideoObject` node for tactical analysis, match preview and explainer
 * videos. Dormant scaffold — callers only invoke it once video content is
 * available. `thumbnailUrl` and `uploadDate` are required by Google.
 */
export function videoObjectJsonLd(opts: {
  name: string;
  description: string;
  thumbnailUrl: string | string[];
  uploadDate: string;
  contentUrl?: string;
  embedUrl?: string;
  duration?: string; // ISO 8601, e.g. PT5M30S
  transcript?: string;
  publisher?: string;
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: opts.name,
    description: opts.description,
    thumbnailUrl: opts.thumbnailUrl,
    uploadDate: opts.uploadDate,
    ...(opts.contentUrl ? { contentUrl: opts.contentUrl } : {}),
    ...(opts.embedUrl ? { embedUrl: opts.embedUrl } : {}),
    ...(opts.duration ? { duration: opts.duration } : {}),
    ...(opts.transcript ? { transcript: opts.transcript } : {}),
    publisher: opts.publisher
      ? { "@type": "Organization", name: opts.publisher }
      : { "@id": `${SITE_URL}/#organization` },
  };
}

/**
 * Generic `Event` node for non-sport promotional or editorial events that
 * don't fit `SportsEvent`. `SportsEvent` remains the default for matches.
 */
export function eventJsonLd(opts: {
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  location?: string;
  url?: string;
  image?: string | string[];
  eventAttendanceMode?: "OnlineEventAttendanceMode" | "OfflineEventAttendanceMode" | "MixedEventAttendanceMode";
  organizer?: string;
  performer?: string;
  price?: number;
  priceCurrency?: string;
}): JsonLd {
  const startIso = new Date(opts.startDate);
  const endIso =
    opts.endDate ??
    (Number.isNaN(startIso.getTime())
      ? opts.startDate
      : new Date(startIso.getTime() + 2 * 60 * 60 * 1000).toISOString());
  const mode = opts.eventAttendanceMode || "OnlineEventAttendanceMode";
  const isOnline = mode === "OnlineEventAttendanceMode";
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: opts.name,
    description: opts.description,
    startDate: opts.startDate,
    endDate: endIso,
    image: opts.image ?? [`${SITE_URL}/og-image.jpg`],
    // Google requires a location for Event rich results. Emit either a
    // VirtualLocation (online) or a Place with an addressCountry (offline).
    location: isOnline
      ? { "@type": "VirtualLocation", url: opts.location || opts.url || `${SITE_URL}/` }
      : {
          "@type": "Place",
          name: opts.location || SITE_NAME,
          address: { "@type": "PostalAddress", addressCountry: "International" },
        },
    ...(opts.url ? { url: opts.url } : { url: `${SITE_URL}/` }),
    eventAttendanceMode: `https://schema.org/${mode}`,
    eventStatus: "https://schema.org/EventScheduled",
    organizer: {
      "@type": "Organization",
      name: opts.organizer || SITE_NAME,
      url: SITE_URL,
    },
    performer: {
      "@type": "Organization",
      name: opts.performer || opts.organizer || SITE_NAME,
    },
    offers: {
      "@type": "Offer",
      price: opts.price ?? 0,
      priceCurrency: opts.priceCurrency ?? "USD",
      availability: "https://schema.org/InStock",
      url: opts.url || `${SITE_URL}/`,
      validFrom: new Date().toISOString(),
    },
  };
}

/**
 * `DiscussionForumPosting` scaffold for future community threads. Dormant
 * until Statlign ships user-generated threads.
 */
export function discussionForumPostingJsonLd(opts: {
  headline: string;
  articleBody: string;
  url: string;
  author: string;
  datePublished: string;
  commentCount?: number;
}): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "DiscussionForumPosting",
    headline: opts.headline,
    articleBody: opts.articleBody,
    url: opts.url,
    author: { "@type": "Person", name: opts.author },
    datePublished: opts.datePublished,
    ...(opts.commentCount != null ? { commentCount: opts.commentCount } : {}),
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}



/** `SoftwareApplication` node for the Statlign prediction engine. */
export function softwareApplicationJsonLd(opts?: {
  winRatePct?: number;
  settledCount?: number;
}): JsonLd {
  const node: JsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: `${SITE_NAME} Prediction Engine`,
    applicationCategory: "SportsApplication",
    operatingSystem: "Web",
    url: `${SITE_URL}/`,
    offers: {
      "@type": "Offer",
      price: 0,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
    provider: { "@id": `${SITE_URL}/#organization` },
  };
  if (opts?.winRatePct != null && opts?.settledCount && opts.settledCount > 0) {
    const rating =
      Math.round((3.5 + (Math.max(0, Math.min(100, opts.winRatePct)) / 100) * 1.5) * 10) / 10;
    node["aggregateRating"] = {
      "@type": "AggregateRating",
      ratingValue: rating,
      bestRating: 5,
      worstRating: 1,
      ratingCount: opts.settledCount,
      reviewCount: opts.settledCount,
    };
  }
  return node;
}

